import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';

// --- SVG ICONS ---
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"></path>
  </svg>
);

// --- MAIN VIEW MODAL COMPONENT ---
const MaterialReturnViewModal = ({ open, onClose, data }) => {
  
  // --- STATE ---
  const initialHeader = {
    MaterialReturnID: null, 
    MaterialReturnNo: '', 
    ReturnDate: new Date().toISOString().split('T')[0],
    VendorCode: '', 
    SurveyBOQID: null,
    SurveyBOQNo: '',
    ProjectHeaderId: '', 
    ProjectNo: '',
    FromFeederId: '', 
    ToFeederId: '', 
    FromFeederCode: '', 
    FromFeederDescription: '',
    ToFeederCode: '', 
    ToFeederDescription: '',
    TransferType: 'Transfer from Contractor',
    Remarks: '', 
    Status: 'Open', 
    ApprovalStatus: '',
    Circle: '', 
    District: '', 
    Division: '', 
    SubDivision: '', 
    SubStation: '',
    vendor_name: '',
    CurrentLevel: 0, 
    SendToBC: false, 
    BcSuccess: false
  };

  const initialLine = {
    MaterialReturnLineID: null, 
    ProductCode: '', 
    Description: '', 
    UnitOfMeasure: 'NOS',
    AvailableQty: 0, 
    Quantity: 0, 
    LineNumber: 1000,
    Remark:''
  };

  const [header, setHeader] = useState(initialHeader);
  const [lines, setLines] = useState([initialLine]);
  const [isMaximized, setIsMaximized] = useState(false);
  const [projectActivities, setProjectActivities] = useState([]);

  // --- RESIZABLE STATE & REFS ---
  const [columnWidths, setColumnWidths] = useState({
    lineNo:150,
    productCode: 250,
    description: 280,
    uom: 100,
    availQty: 120,
    qty: 120,
    remark:280,
    activity: 200,
    subActivity: 200
  });

  const resizingRef = useRef(null);

  useEffect(() => {
    if (open && data && data.header && data.header.ProjectNo) {
      const fetchActivities = async () => {
        try {
          const token = localStorage.getItem('authToken');
          // Fetch activities for this project
          const res = await fetch(`/api/activity-master/project/${data.header.ProjectNo}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const result = await res.json();
          setProjectActivities(Array.isArray(result) ? result : []);
        } catch (e) {
          console.error("Failed to fetch activities", e);
          setProjectActivities([]);
        }
      };

      fetchActivities();
    }
  }, [open, data]);
  
  // --- EFFECTS ---
  useEffect(() => {
    if (open && data) {
      setIsMaximized(false);
      
      const sourceHeader = data.header || {};
      
      // Populate Header
      setHeader({
        ...initialHeader,
        ...sourceHeader,
        ReturnDate: sourceHeader.ReturnDate ? sourceHeader.ReturnDate.split('T')[0] : new Date().toISOString().split('T')[0]
      });

      // Populate Lines
      const sourceLines = data.lines || [];
      if (sourceLines.length > 0) {
        setLines(sourceLines.map(l => ({ ...initialLine, ...l })));
      } else {
        setLines([initialLine]);
      }
    }
  }, [open, data]);

   

  // --- RESIZABLE HANDLERS ---
  const startResize = (columnKey, startX) => {
    resizingRef.current = {
      columnKey,
      startX,
      startWidth: columnWidths[columnKey]
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResize);
  };

  const handleMouseMove = (e) => {
    if (!resizingRef.current) return;

    const { columnKey, startX, startWidth } = resizingRef.current;
    const delta = e.clientX - startX;

    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: Math.max(60, startWidth + delta)
    }));
  };

  const stopResize = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', stopResize);
    resizingRef.current = null;
  };

  // --- RESIZABLE TH COMPONENT ---
  const ResizableTh = ({ width, onResize, children, align = 'left' }) => (
    <th
      style={{ width }}
      className={`relative p-2 border ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'} select-none`}
    >
      {children}
      <div
        className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400"
        onMouseDown={(e) => onResize(e.clientX)}
      />
    </th>
  );

  if (!open) return null;

  return (
    <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
      <style>{`
        .hide-scrollbar::-webkit-scrollbar{display:none}
        
        .resizable-table {
          table-layout: fixed;
          min-width: max-content;
        }
      `}</style>
      
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      
      <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'} ${isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl'}`} tabIndex={0}>
        
        {/* --- MODAL HEADER --- */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">View Material Return</h2>
            <div className="flex items-center gap-2">
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={() => setIsMaximized(!isMaximized)}
              >
                {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
              </button>
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={onClose}
              >
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>

        {/* --- VIEW BODY --- */}
        <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
          <div className="space-y-6 pb-6">
            
            {/* --- HEADER SECTION --- */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <h3 className="text-lg font-semibold text-[#384959] mb-4">Return Information</h3>
              
              {/* Grid Layout: 2 Columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* Row 1 */}
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Return No</label>
                  <input 
                    type="text" 
                    value={header.MaterialReturnNo} 
                    disabled 
                    className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase-input text-gray-600" 
                  />
                </div>
                
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Date</label>
                  <input 
                    type="text" 
                    value={header.ReturnDate ? dayjs(header.ReturnDate).format('DD/MM/YYYY') : ''} 
                    disabled 
                    className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] text-gray-600" 
                  />
                </div>

                {/* Row 2 */}
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Vendor / Contractor</label>
                  <input 
                    type="text" 
                    value={header.vendor_name || header.VendorCode} 
                    disabled 
                    className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] text-gray-600" 
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Project</label>
                  <input 
                    type="text" 
                    value={`${header.ProjectNo || ''} - ${header.ProjectDescription || ''}`}
                    disabled 
                    className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] text-gray-600" 
                  />
                </div>

                {/* Row 3 - Feeder Details */}
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">From Feeder (Source)</label>
                  <input 
                    type="text" 
                    value={header.FromFeederCode && header.FromFeederDescription ? `${header.FromFeederCode} - ${header.FromFeederDescription}` : ''} 
                    disabled 
                    className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] text-gray-600" 
                  />
                </div>
<div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">
    Survey BOQ
  </label>
  <input
    type="text"
    value={header.SurveyBOQNo || ''}
    disabled
    className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] text-gray-600"
  />
</div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">To Feeder (Destination)</label>
                  <input 
                    type="text" 
                    value={header.ToFeederCode && header.ToFeederDescription ? `${header.ToFeederCode} - ${header.ToFeederDescription}` : ''} 
                    disabled 
                    className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] text-gray-600" 
                  />
                </div>

                {/* Row 4 - Auto-filled Details */}
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Circle</label>
                  <input 
                    type="text" 
                    value={header.Circle || ''} 
                    readOnly 
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-600" 
                  />
                </div>
                
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">District</label>
                  <input 
                    type="text" 
                    value={header.District || ''} 
                    readOnly 
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-600" 
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Division</label>
                  <input 
                    type="text" 
                    value={header.Division || ''} 
                    readOnly 
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-600" 
                  />
                </div>
                
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Sub Division</label>
                  <input 
                    type="text" 
                    value={header.SubDivision || ''} 
                    readOnly 
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-600" 
                  />
                </div>
                
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Sub Station</label>
                  <input 
                    type="text" 
                    value={header.SubStation || ''} 
                    readOnly 
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-600" 
                  />
                </div>

                {/* Status - Full Width */}
                <div className="flex flex-col gap-2 md:col-span-2">
                  <label className="block text-sm font-medium text-[#64748b]">Status</label>
                  <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-[#F1F5F9]">
                    {!header.MaterialReturnID ? (
                      <span className="text-xs font-medium text-gray-600 italic">Open</span> 
                    ) : header.ApprovalStatus === 'PENDING' ? (
                      <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                        Pending Approval (Level {header.CurrentLevel || 1})
                      </span>
                    ) : header.ApprovalStatus === 'REJECTED' ? (
                      <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">
                        Rejected
                      </span>
                    ) : header.ApprovalStatus === 'APPROVED' ? (
                      header.SendToBC === 1 || header.SendToBC === true ? (
                        <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200">
                          Sent to BC Successfully
                        </span>
                      ) : (
                        <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-200">
                          Approved (Ready to Send BC)
                        </span>
                      )
                    ) : (
                      <span className="text-xs text-gray-500 italic">
                        {(header.SendToBC === 1 || header.SendToBC === true) ? 'Sent to BC Successfully' : 'Open'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Remarks - Full Width */}
                <div className="flex flex-col gap-2 md:col-span-2">
                  <label className="block text-sm font-medium text-[#64748b]">Remarks</label>
                  <textarea 
                    value={header.Remarks || ''} 
                    disabled 
                    rows="2" 
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-[#F1F5F9] text-gray-600 resize-none"
                  ></textarea>
                </div>
              </div>
            </div>

            {/* --- LINES SECTION --- */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#384959]">Return Lines</h3>
              </div>
              
              <div className="overflow-x-auto">
                <table className="resizable-table w-full border border-[#E2E8F0]">
                  <thead>
                    <tr className="bg-[#BDDDFC] text-[#384959]">
                      <ResizableTh width={columnWidths.lineNo} onResize={(x) => startResize('lineNo', x)} align="center">
                        Line No
                      </ResizableTh>
                      <ResizableTh width={columnWidths.productCode} onResize={(x) => startResize('productCode', x)}>
                        Product Code
                      </ResizableTh>
                      <ResizableTh width={columnWidths.description} onResize={(x) => startResize('description', x)}>
                        Description
                      </ResizableTh>
                      <ResizableTh width={columnWidths.uom} onResize={(x) => startResize('uom', x)}>
                        UOM
                      </ResizableTh>
                       <ResizableTh width={columnWidths.activity} onResize={(x) => startResize('activity', x)}>
                        Activity
                      </ResizableTh>
                      <ResizableTh width={columnWidths.subActivity} onResize={(x) => startResize('subActivity', x)}>
                        Sub Activity
                      </ResizableTh>
                      <ResizableTh width={columnWidths.availQty} onResize={(x) => startResize('availQty', x)} align="right">
                        Avail Qty
                      </ResizableTh>
                      <ResizableTh width={columnWidths.qty} onResize={(x) => startResize('qty', x)} align="right">
                        Qty
                      </ResizableTh>
                      <ResizableTh width={columnWidths.remark} onResize={(x) => startResize('qty', x)} align="right">
                        Remark
                      </ResizableTh>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line, idx) =>{
                        const activityObj = projectActivities.find(a => a.ActivityId == line.ActivityId);
                       const activityDisplay = activityObj 
                           ? `${activityObj.Code} - ${activityObj.Description}` 
                           : '';

                       const subActivityObj = projectActivities.find(a => a.ActivityId == line.SubActivityId);
                       const subActivityDisplay = subActivityObj 
                           ? `${subActivityObj.Code} - ${subActivityObj.Description}` 
                           : '';
                            
                      return(
                      <tr key={idx} className="border-t border-[#E2E8F0] hover:bg-white transition-colors">
                        <td className="p-2 border" style={{ width: columnWidths.lineNo }}>
                          <input 
                            type="number" 
                            value={line.LineNumber} 
                            className="w-full border-0 px-2 py-1 text-center bg-transparent" 
                            readOnly 
                          />
                        </td>
                        <td className="p-2 border" style={{ width: columnWidths.productCode }}>
                          <input 
                            type="text" 
                            value={line.ProductCode} 
                            className="w-full border-0 px-2 py-1 bg-transparent" 
                            readOnly 
                          />
                        </td>
                        <td className="p-2 border" style={{ width: columnWidths.description }}>
                          <input 
                            type="text" 
                            value={line.Description || line.ProductName} 
                            className="w-full border-0 px-2 py-1 bg-transparent" 
                            readOnly 
                          />
                        </td>
                        <td className="p-2 border" style={{ width: columnWidths.uom }}>
                          <input 
                            type="text" 
                            value={line.UnitOfMeasure} 
                            className="w-full border-0 px-2 py-1 bg-transparent uppercase" 
                            readOnly 
                          />
                        </td>
                         <td className="p-2 border" style={{ width: columnWidths.activity }}>
                          <input 
                            type="text" 
                            // CHANGE: Use 'activityDisplay' instead of line.ActivityCode
                            value={activityDisplay} 
                            className="w-full border-0 px-2 py-1 bg-transparent" 
                            readOnly 
                          />
                        </td>

                        {/* --- SUB-ACTIVITY CELL (FIXED) --- */}
                        <td className="p-2 border" style={{ width: columnWidths.subActivity }}>
                          <input 
                            type="text" 
                            // CHANGE: Use 'subActivityDisplay' instead of line.SubActivityCode
                            value={subActivityDisplay} 
                            className="w-full border-0 px-2 py-1 bg-transparent" 
                            readOnly 
                          />
                        </td>
                        <td className="p-2 border" style={{ width: columnWidths.availQty }}>
                          <input 
                            type="number" 
                            value={line.AvailableQty} 
                            className="w-full border-0 px-2 py-1 text-right font-medium text-blue-800" 
                            readOnly 
                          />
                        </td>
                        <td className="p-2 border" style={{ width: columnWidths.qty }}>
                          <input 
                            type="number" 
                            value={line.Quantity} 
                            className="w-full border-0 px-2 py-1 text-right font-semibold text-gray-800" 
                            readOnly 
                          />
                        </td>
                        <td className="p-2 border" style={{ width: columnWidths.qty }}>
                          <input 
                            type="text" 
                            value={line.Remark} 
                            className="w-full border-0 px-2 py-1" 
                            readOnly 
                          />
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* --- FOOTER --- */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button 
              type="button" 
              className="px-6 py-3 rounded-lg bg-[#6A89A7] text-white font-semibold transition-all duration-300 ease-in-out hover:bg-[#88BDF2]" 
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default MaterialReturnViewModal;